import { Component, inject, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { BaseComponent } from '@app/finxone-web-frontend/app/Modules/shared/widgets/base/base.component';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { GetBeneficiaryRequiredFieldsAction } from '@app/finxone-web-frontend/app/lib/actions/beneficiary.action';
import {
  SetCopResponseStateMatchStatusCode,
  SetCopResponseStateName,
} from '@app/finxone-web-frontend/app/lib/actions/cop-response.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import {
  CheckPayeeResponse,
  Payee,
} from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import {
  AccountState,
  AccountStateModel,
  PaymentGatewayConfig,
} from '@app/finxone-web-frontend/app/lib/state/account.state';
import {
  FormActionState,
  FormActionType,
} from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { getCountryCodeFromBeneficiaryAccountDetail } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/domestic-payments/add-beneficiary';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { FormActionTypeEnum, OtpParam } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ACCOUNTTYPE,
  APP_ZONES,
  AccountInfoType,
  BaseCssProperties,
  CheckPayeeMatchStatus,
  PaymentFlowTypes,
  ProviderClass,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
  cssToNgStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import * as ibantools from 'ibantools';
import {
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
  throwError,
} from 'rxjs';
import { setNoMatchReasonFromResponse } from '../../add-beneficiary-v2/beneficiary-v2.utils';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';
interface BeneficiaryDetailFormType {
  name?: FormControl<string>;
  firstName?: FormControl<string>;
  lastName?: FormControl<string>;
  iban: FormControl<string>;
  bic?: FormControl<string | null>;
}
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-add-beneficiary-sepa',
  templateUrl: './add-beneficiary-sepa.component.html',
})
export class AddBeneficiarySepaComponent extends BaseComponent implements OnInit {
  @Input() formActionStateData: { [key: string]: string };
  @Input() dynamicZone = false;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public buttonContent = '';
  public beneficiaryDetailForm: FormGroup<BeneficiaryDetailFormType>;
  payeeEnabled: boolean;
  @Select(AccountState.getAccounts)
  private accounts!: Observable<AccountStateModel>;
  @Select(FormActionState.getFormActionStateWithId(FormActionTypeEnum.ADD_BENEFICIARY))
  formAction!: Observable<FormActionType>;
  @Select(AccountState.getAccounts)
  accountsData$!: Observable<AccountStateModel>;
  accountId: string;
  public beneficiaryType: string;
  public ACCOUNTTYPE = ACCOUNTTYPE;
  public submitButtonStyle: baseCssConfigStyle = {};
  public enableBeneficiaryFirstAndLastName = false;
  public name: string;
  public lastName?: string;
  public firstName?: string;
  public isAllowedGlobalStyling = false;
  public globalStylingClass = '';
  public colorTextAlignStyle: baseCssConfigStyle;
  public iban: string;
  public bic: string;
  public selectedAccount: AccountInfoType | undefined;
  public bankProvider = 'railsr';

  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    private fb: FormBuilder,
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    private configService: ConfigService,
    private store: Store,
    private router: Router,
    private alertHandlerService: AlertHandlerService,
    public formSubmissionService: FormSubmissionService,
    private accountService: AccountService,
  ) {
    super();
    this.selectedAccount = this.store.selectSnapshot(AccountState.getCurrentAccount);
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.BORDER,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
    ]);
    this.colorTextAlignStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);
    this.beneficiaryType = this.dataIn.attri.widgetProperties['beneficiaryType'];
    this.submitButtonStyle = this.configService.getSpecificBaseCssStyle(
      this.getNestedWidgetConfig(dataIn, 'submitButton'),
      buttonNestedWidgetConfigStyles,
    );

    this.enableBeneficiaryFirstAndLastName = this.store.selectSnapshot(
      ProjectSettingsState.getProjectSettings,
    ).enableBeneficiaryFirstAndLastName;
    this.getFormValuesFromState();
    this.buildAddBeneficiarySepaForm();

    this.accounts
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$),
        map((account) => {
          this.accountId = account.currentAccountId;
        }),
        catchError((error) => {
          this.alertHandlerService.showAlertFn('error', error.error.message);
          return throwError(() => error);
        }),
      )
      .subscribe();
  }

  private buildAddBeneficiarySepaForm() {
    const commonControls = {
      iban: new FormControl(this.iban, {
        nonNullable: true,
        validators: [Validators.required, this.ibanValidator()],
      }),
      bic: new FormControl(this.bic, {
        nonNullable: false,
        validators: [this.bicValidator()],
      }),
    };
    if (
      !this.enableBeneficiaryFirstAndLastName ||
      (this.enableBeneficiaryFirstAndLastName && this.beneficiaryType === ACCOUNTTYPE.BUSINESS)
    ) {
      this.beneficiaryDetailForm = this.fb.group<BeneficiaryDetailFormType>({
        name: new FormControl(this.name, {
          nonNullable: true,
          validators: [Validators.required, Validators.pattern(/^[A-Za-z\s-.’]+$/)],
        }),
        ...commonControls,
      });
    } else if (this.enableBeneficiaryFirstAndLastName && this.beneficiaryType === ACCOUNTTYPE.PERSONAL) {
      this.beneficiaryDetailForm = this.fb.group<BeneficiaryDetailFormType>({
        firstName: new FormControl(this.firstName ?? '', {
          nonNullable: true,
          validators: [Validators.required, Validators.pattern(/^[A-Za-z\s-.’]+$/)],
        }),
        lastName: new FormControl(this.lastName ?? '', {
          nonNullable: true,
          validators: [Validators.required, Validators.pattern(/^[A-Za-z\s-.’]+$/)],
        }),
        ...commonControls,
      });
    }
  }

  ngOnInit() {
    if (this.dynamicZone && this.formActionStateData !== undefined) {
      this.patchAddBeneficiaryForm();
      this.editBeneficiaries();
    }
    this.buttonContent =
      this.dataIn.widgetConfig?.nestedWidgets?.['submitButton'].widgetProperties.textContent ?? 'Proceed';
    this.getGlobalStyling();
    this.onGetPaymentGateway();
    this.valueChangesTrigger();
  }

  private onGetPaymentGateway(): void {
    this.store
      .selectOnce(AccountState.getPaymentGatewayConfig)
      .pipe(
        map((paymentGatewayConfig: PaymentGatewayConfig) => {
          // TODO add other providers if needed when/if they support payee checking
          this.payeeEnabled =
            paymentGatewayConfig?.checkPayeeEnabled && paymentGatewayConfig?.bankProvider === 'andaria';
          this.bankProvider = paymentGatewayConfig?.bankProvider ?? 'railsr';
        }),
      )
      .subscribe();
  }

  private valueChangesTrigger() {
    this.beneficiaryDetailForm.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((values) => {
        if (this.dynamicZone) {
          this.editBeneficiaries();
        }
      });
  }

  private editBeneficiaries() {
    const fullNameValue = `${this.beneficiaryDetailForm.value?.firstName ?? ''} ${
      this.beneficiaryDetailForm.value?.lastName ?? ''
    }`.trim();

    const payload = {
      accountType: this.formActionStateData?.['accountType'],
      bankId: this.formActionStateData?.['bankId'],
      id: this.formActionStateData?.['bid'],
      name: this.beneficiaryDetailForm?.get('name')?.value ?? fullNameValue,
      destinationIdentifier: {
        currency: this.formActionStateData?.['currency'],
        bic: this.beneficiaryDetailForm.value.bic,
        iban: this.beneficiaryDetailForm.value.iban,
        id: this.formActionStateData?.['bankId'],
        country: getCountryCodeFromBeneficiaryAccountDetail(
          'iban',
          this.beneficiaryDetailForm.value?.iban ?? '',
        ),
      },
      isBeneficiaryFormValid: this.beneficiaryDetailForm.valid,
    };
    if (
      this.beneficiaryDetailForm.valid &&
      payload.destinationIdentifier.country &&
      payload.destinationIdentifier.currency
    ) {
      this.store.dispatch(
        new GetBeneficiaryRequiredFieldsAction(
          payload.destinationIdentifier.country,
          payload.destinationIdentifier.currency,
        ),
      );
    }
    const formObject = this.beneficiaryDetailForm.value;
    if ('firstName' in formObject && 'lastName' in formObject) {
      const firstName = formObject['firstName'];
      const lastName = formObject['lastName'];
      const fullName = firstName + ' ' + lastName;
      // Remove firstName and lastName from formObject
      delete formObject['firstName'];
      delete formObject['lastName'];
      // Add fullName under name key
      formObject['name'] = fullName;
    }
    this.formSubmissionService.submitIndividualForm(
      {
        ...formObject,
      },
      '',
    );

    this.formSubmissionService.submitIndividualForm(
      {
        payload,
      },
      '',
      FormActionTypeEnum.EDIT_BENEFICIARY,
    );
  }

  private patchAddBeneficiaryForm(): void {
    const name = this.formActionStateData?.['name'].trim();
    let firstName = '';
    let lastName = '';
    if (name) {
      const fullName = name.split(' ');
      if (fullName?.length > 1) {
        lastName = fullName[fullName?.length - 1];
        firstName = fullName.slice(0, -1).join(' '); // All words except the last as given name
      } else {
        firstName = fullName[0];
      }
    }
    this.beneficiaryDetailForm.patchValue({
      name: this.formActionStateData?.['name'],
      firstName: firstName,
      lastName: lastName,
      iban: this.formActionStateData?.['iban'],
      bic: this.formActionStateData?.['bic'],
    });
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['submitButton'].widgetProperties?.['globalStylingClass'] &&
      this.dataIn.widgetConfig.nestedWidgets?.['submitButton'].widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['submitButton'].widgetProperties?.['globalStylingClass'] ??
        '';
      this.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['submitButton'].widgetProperties?.['globalStyling'] ?? false;
    }
  }

  private getFormValuesFromState() {
    this.formAction.pipe(takeUntil(this.destroy$)).subscribe((formValues: FormActionType) => {
      this.bic = formValues?.formData?.['destinationIdentifier']?.['bic'] ?? '';
      this.iban = formValues?.formData?.['destinationIdentifier']?.['iban'] ?? '';
      this.name = formValues?.formData?.['name'] ?? '';
      const name = this.name.trim();
      if (name) {
        const fullName = name.split(' ');

        if (fullName?.length > 1) {
          this.lastName = fullName[fullName?.length - 1];
          this.firstName = fullName.slice(0, -1).join(' '); // All words except the last as given name
        } else {
          this.firstName = fullName[0];
        }
      }
    });
  }

  public submitAddBeneficiaryForm() {
    if (this.beneficiaryDetailForm.valid) {
      const fullNameValue = `${this.beneficiaryDetailForm.value?.firstName ?? ''} ${
        this.beneficiaryDetailForm.value?.lastName ?? ''
      }`.trim();
      const ibanString = this.beneficiaryDetailForm.controls['iban']?.value;

      const formData = {
        name: this.beneficiaryDetailForm.value?.name ?? fullNameValue,
        accountType: this.beneficiaryType,
        accountId: this.accountId,
        destinationIdentifier: {
          bic: this.beneficiaryDetailForm.value.bic,
          iban: this.beneficiaryDetailForm.value.iban,
          currency: 'EUR',
          country: getCountryCodeFromBeneficiaryAccountDetail('iban', ibanString),
        },
      };
      this.store.dispatch(
        new GetBeneficiaryRequiredFieldsAction(
          formData.destinationIdentifier.country,
          formData.destinationIdentifier.currency,
        ),
      );
      this.formSubmissionService.submitIndividualForm(formData, '', FormActionTypeEnum.ADD_BENEFICIARY);
      this.router.navigateByUrl(
        `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/beneficiary-address?paymentflow=${
          PaymentFlowTypes.SEPA
        }`,
      );
    } else {
      this.alertHandlerService.showAlertFn(
        'error',
        'Please make sure all the necessary details are provided.',
      );
    }
  }

  // TODO enable vop
  // public submitAddBeneficiaryForm() {
  //   if (this.beneficiaryDetailForm.valid) {
  //     this.setAddBeneficiaryFormState();
  //     this.vopCheck(OtpParam.AddBeneficiary);
  //   } else {
  //     this.alertHandlerService.showAlertFn(
  //       'error',
  //       'Please make sure all the necessary details are provided.',
  //     );
  //   }
  // }

  private setAddBeneficiaryFormState() {
    const fullNameValue = `${this.beneficiaryDetailForm.value?.firstName ?? ''} ${
      this.beneficiaryDetailForm.value?.lastName ?? ''
    }`.trim();
    const ibanString = this.beneficiaryDetailForm.controls['iban']?.value;

    const formData = {
      name: this.beneficiaryDetailForm.value?.name ?? fullNameValue,
      accountType: this.beneficiaryType,
      accountId: this.accountId,
      destinationIdentifier: {
        bic: this.beneficiaryDetailForm.value.bic,
        iban: this.beneficiaryDetailForm.value.iban,
        currency: 'EUR',
        country: getCountryCodeFromBeneficiaryAccountDetail('iban', ibanString),
      },
    };
    this.store.dispatch(
      new GetBeneficiaryRequiredFieldsAction(
        formData.destinationIdentifier.country,
        formData.destinationIdentifier.currency,
      ),
    );
    this.formSubmissionService.submitIndividualForm(formData, '', FormActionTypeEnum.ADD_BENEFICIARY);
  }

  private vopCheck(param: string = OtpParam.AddBeneficiary) {
    if (this.payeeEnabled) {
      const formObject = this.beneficiaryDetailForm.value;
      if (
        formObject?.['firstName'] &&
        formObject?.['lastName'] &&
        this.beneficiaryType === ACCOUNTTYPE.PERSONAL
      ) {
        // Add fullName under name key
        formObject['name'] = formObject['firstName'] + ' ' + formObject['lastName'];

        // Remove firstName and lastName from formObject
        delete formObject['firstName'];
        delete formObject['lastName'];
      }

      this.accountService
        .checkPayee(
          {
            name: formObject['name'],
            iban: this.beneficiaryDetailForm.value.iban,
            accountType: this.beneficiaryType as ACCOUNTTYPE,
          } as Payee,
          param,
        )
        .subscribe((checkPayeeResponse: CheckPayeeResponse) => {
          this.store.dispatch(new SetCopResponseStateName(checkPayeeResponse.name));
          this.store.dispatch(new SetCopResponseStateMatchStatusCode(checkPayeeResponse.matchStatusCode));
          if (checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.MATCHED) {
            this.router.navigateByUrl(
              `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-match-cop?flow=${param}`,
            );
          } else if (
            checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.CLOSE_MATCH ||
            checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.NAME_MATCHED ||
            checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.ACCOUNT_TYPE_MISMATCH_NAME_CLOSE_MATCH
          ) {
            this.router.navigateByUrl(
              `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-close-match-cop?flow=${param}`,
            );
          } else {
            setNoMatchReasonFromResponse(checkPayeeResponse.matchStatusCode, this.store);
            this.router.navigateByUrl(
              `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-no-match-cop?flow=${param}`,
            );
          }
        });
    } else {
      // don't check the payee
      this.router.navigateByUrl(
        `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-match?flow=${param}`,
      );
    }
  }

  public ibanValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const ibanValue = control.value;
      const isValid = ibantools.isValidIBAN(ibanValue);
      if (!isValid) {
        return { invalidIban: true };
      }
      return null;
    };
  }

  public bicValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const bicValue = control.value;
      const isValid = ibantools.isValidBIC(bicValue);
      if (!isValid && bicValue?.length) {
        return { invalidBic: true };
      }
      return null;
    };
  }

  public get nestedButtonParentCss() {
    const nestedWidgetAttri = this.dataIn.widgetConfig.nestedWidgets?.['submitButton'];
    return cssToNgStyle(nestedWidgetAttri?.['nestedButtonParentCss'] ?? '');
  }
}
